import { CANONICAL_ITEMS } from "./generated/items";
import { OBTAIN_METHODS_BY_ITEM_ID } from "./generated/obtain-methods";
import { SHOP_LISTINGS_BY_SHOP_ID } from "./generated/entity-indexes";
import type { CanonicalItem, ItemKind } from "./schema/types";

/**
 * Item pages with nothing to say — withheld from the index (see CLAUDE.md, "Cutting index bloat").
 *
 * Shared by the Node build scripts and the app so the rule cannot drift, the way `locales` did:
 * scripts import it as `../src/data/thin-items`, the app re-exports it through `lib/data.ts`
 * (still the only module allowed to reach the data layer by relative path).
 *
 * ## Why these five kinds and not the other nine
 *
 * The obvious definition — "no sellPrice, no obtain method, no shop listing" — matches 125 items,
 * and applying it wholesale would have been a mistake. Measuring the prerendered `#root` text of
 * every item page in `dist` on 2026-08-25 (median characters, tags stripped):
 *
 *   flower 2161 (n=40) · dish 1679 (n=12) · animal 1175 (n=9) · forageable 1152 (n=7)
 *   seed 1041 (n=24) · ingredient 982 (n=2) · material 961 (n=2) · tool 943 (n=22) · furniture 929 (n=7)
 *
 * against a median of 1760 for items that DO have price + obtain. The 40 stat-less flowers render
 * *more* than a fully-populated page, because they inherit the colour-variant sibling grid; dishes
 * carry their recipe, animals their map and location. Only the five kinds below actually bottom out
 * at ~930-1040 characters — a name, an image and one line of flavour text.
 *
 * Every one of the 747 items has a real description (733 are >20 chars), so none of these pages is
 * literally empty. They are thin, not blank, which is why the cut is narrow and evidence-led rather
 * than a sweep: 57 items, and all 57 earned exactly zero clicks in the 28 days to 2026-08-23 — a
 * window that includes the site's healthy period, not just the post-16/08 collapse.
 *
 * Widening this set is a data decision, not a code one: give a tool a real sellPrice or obtain
 * method and its page leaves the set automatically on the next build.
 */
const THIN_KINDS: ReadonlySet<ItemKind> = new Set<ItemKind>(["tool", "furniture", "seed", "material", "ingredient"]);

/**
 * Item ids sold by at least one shop **at a real price**. Flattened here because the generated
 * indexes key listings by shop, and this is the only place that needs the inverse.
 *
 * The `price > 0` guard is what stops this rule from being gamed by a placeholder. `validate-data.ts`
 * checks only that a listing's `itemId` resolves — it never looks at `price` — so a row added with
 * `price: 0` or `price: null` while someone is still collecting real numbers would otherwise lift
 * the page straight back into the sitemap advertising a price nobody has confirmed. That is worse
 * than the thin page it replaces: a thin page is merely unhelpful, a fabricated price is wrong.
 *
 * One listing in the current data already has `price: 0` (`shop-andrew-listing-3`,
 * `green-motorcycle`), so this is a live case, not a hypothetical. It changes nothing today —
 * `vehicle` is not a thin kind — but it means a half-filled row can never quietly publish.
 */
const ITEM_IDS_WITH_SHOP_LISTING: ReadonlySet<string> = new Set(
  Object.values(SHOP_LISTINGS_BY_SHOP_ID)
    .flat()
    .filter((listing) => typeof listing.price === "number" && Number.isFinite(listing.price) && listing.price > 0)
    .map((listing) => listing.itemId),
);

/**
 * True when this item's detail page has no stat worth indexing: a `THIN_KINDS` page with no
 * populated stat. Any of sellPrice, an obtain method, or a shop listing gives the page a fact a
 * reader could have searched for.
 *
 * A second rule withheld every undescribed creature and dish page the game-client import published,
 * from 2026-09-12 until it was reverted, on the theory that those pages caused the 2026-08-16
 * traffic drop. The import was not in production until 2026-09-01, none of its 409 pages ever earned
 * an impression, and the drop hit every page group on the same day. See
 * docs/research/the-16-08-demotion-cause/research.md before adding a rule keyed on provenance.
 */
export function isThinItemPage(item: Pick<CanonicalItem, "id" | "kind" | "sellPrice">): boolean {
  if (!THIN_KINDS.has(item.kind)) return false;
  if (item.sellPrice != null) return false;
  if (OBTAIN_METHODS_BY_ITEM_ID[item.id]?.length) return false;
  if (ITEM_IDS_WITH_SHOP_LISTING.has(item.id)) return false;
  return true;
}

/** Precomputed for callers that only have an id (the prerender, the detail route). */
export const THIN_ITEM_IDS: ReadonlySet<string> = new Set(
  CANONICAL_ITEMS.filter(isThinItemPage).map((item) => item.id),
);

/**
 * True for a category route whose kind has no items at all — `/clothes` and `/special` today.
 *
 * `router.tsx` emits a list route for every kind in the `ItemKind` union, including ones the
 * catalog has never covered, so these render a page titled "All Clothes in Heartopia — Complete
 * List" above an empty grid. Measured against `dist`: 544 characters of text, well under the 929
 * of the thinnest item page. The title makes a promise the page does not keep, which is the
 * definition of a soft 404 — and nothing in the site's navigation links to either one, so they
 * exist only as an artifact of that route map.
 *
 * `generate-sitemap.ts` already excludes them from the sitemap on the same condition (see
 * STATIC_ENTRIES). This adds the other half: the pages themselves say `noindex`, so a stale
 * inbound link or an old crawl cannot keep them in the index.
 *
 * Dynamic on purpose, exactly like the sitemap filter: a kind rejoins the index on the next build
 * the moment it gains its first item. `material` did precisely that on 2026-08-05.
 */
export function isEmptyItemKind(kind: ItemKind): boolean {
  return !CANONICAL_ITEMS.some((item) => item.kind === kind);
}
